import React, {ReactElement, useLayoutEffect, useState} from 'react';
import {StoreAddress} from 'models/storeAddress';
import {Controller, useFormContext} from 'react-hook-form';
import {
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography
} from '@mui/material';
import Label from '../Label';
import states from './ShippableUSStates';
import {useGetShippableCountriesQuery} from '../../api/storesApi';

interface ShippingDetailsProps {
  storeSlug: string;
  storeAddressData: StoreAddress[];
}

const ShippingDetails: React.FC<ShippingDetailsProps> = ({
  storeSlug,
  storeAddressData
}: ShippingDetailsProps): ReactElement => {
  const hasStoreAddress = storeAddressData.length > 0;

  const {control, getValues, setValue} = useFormContext();

  const [selectedCountry, setSelectedCountry] = useState<string>(
    getValues('shippingCountry')
  );

  const {
    data: shippableCountriesData,
    isLoading: isShippableCountriesDataLoading
  } = useGetShippableCountriesQuery({
    storeSlug
  });

  const handleCountrySelect = (
    selectEvent: SelectChangeEvent,
    onChange: (...event: any[]) => void
  ) => {
    setValue('shippingState', '');
    onChange(selectEvent);
    setSelectedCountry(selectEvent.target.value);
  };

  // Scroll to shipping details if url has #shipping-details
  useLayoutEffect(() => {
    if (window.location.hash === '#shipping-details') {
      const anchorElement = document.getElementById('shipping-details');
      const scrollableElement = document.getElementsByClassName(
        'organizerLayoutWrapper'
      )[0];
      if (anchorElement) {
        const headerHeight =
          document.getElementsByClassName('header')[0]?.clientHeight ?? 0;
        const statusMessageHeight =
          document.getElementsByClassName('storeStatusBar')[0]?.clientHeight ??
          0;
        const groupMessageHeight =
          document.getElementsByClassName('groupStatusBar')[0]?.clientHeight ??
          0;

        const offsetY =
          anchorElement.offsetTop -
          statusMessageHeight -
          headerHeight -
          groupMessageHeight -
          20;

        scrollableElement.scrollTop = offsetY;
      }
    }
  }, []);

  return (
    <div className="form-field shipping-details">
      <div className="tab-subheading" id="shipping-details">
        <Typography variant="subHeading">
          {hasStoreAddress ? 'Shipping Address' : 'Enter Your Shipping Address'}
        </Typography>
      </div>
      <div className="form-field-split">
        <div className="split-input">
          <Label required>First Name</Label>
          <Controller
            render={({field}) => (
              <TextField
                value={field.value}
                onChange={field.onChange}
                inputRef={field.ref}
                autoComplete="shipping given-name"
                required
              />
            )}
            name="shippingFirstName"
            control={control}
          />
        </div>
        <div className="split-input">
          <Label required>Last Name</Label>
          <Controller
            render={({field}) => (
              <TextField
                value={field.value}
                onChange={field.onChange}
                inputRef={field.ref}
                autoComplete="shipping family-name"
                required
              />
            )}
            name="shippingLastName"
            control={control}
          />
        </div>
      </div>
      <div className="form-field">
        <Label required>Shipping Address</Label>
        <Controller
          render={({field}) => (
            <TextField
              value={field.value}
              onChange={field.onChange}
              inputRef={field.ref}
              autoComplete="shipping street-address"
              required
            />
          )}
          name="shippingAddressOne"
          control={control}
        />
      </div>
      <div className="form-field">
        <Label>Apt/Suite/Floor</Label>
        <Controller
          render={({field}) => (
            <TextField
              value={field.value}
              onChange={field.onChange}
              inputRef={field.ref}
              autoComplete="shipping address-line2"
            />
          )}
          name="shippingAddressTwo"
          control={control}
        />
      </div>
      <div className="form-field-split">
        <div className="split-input">
          <Label required>City</Label>
          <Controller
            render={({field}) => (
              <TextField
                value={field.value}
                onChange={field.onChange}
                inputRef={field.ref}
                autoComplete="shipping locality"
                required
              />
            )}
            name="shippingCity"
            control={control}
          />
        </div>
        <div className="split-input">
          <Label required>Postal Code</Label>
          <Controller
            render={({field}) => (
              <TextField
                value={field.value}
                onChange={field.onChange}
                inputRef={field.ref}
                autoComplete="shipping postal-code"
                required
              />
            )}
            name="shippingPostalCode"
            control={control}
          />
        </div>
      </div>
      <div className="form-field-split">
        <div className="split-input">
          <Label required>State</Label>
          <Controller
            render={({field}) =>
              selectedCountry === 'US' ? (
                <Select
                  value={field.value}
                  onChange={field.onChange}
                  inputRef={field.ref}
                  autoComplete="shipping region"
                  required>
                  {states.map(s => (
                    <MenuItem value={s.code} key={s.code}>
                      {s.name}
                    </MenuItem>
                  ))}
                </Select>
              ) : (
                <TextField
                  value={field.value}
                  onChange={field.onChange}
                  inputRef={field.ref}
                  autoComplete="shipping region"
                  required
                />
              )
            }
            name="shippingState"
            control={control}
          />
        </div>
        <div className="split-input">
          <Label required>Country</Label>
          <Controller
            render={({field}) =>
              isShippableCountriesDataLoading ? (
                <TextField
                  value={field.value}
                  onChange={field.onChange}
                  inputRef={field.ref}
                  required
                  disabled
                />
              ) : (
                <Select
                  value={field.value}
                  onChange={event => handleCountrySelect(event, field.onChange)}
                  inputRef={field.ref}
                  autoComplete="shipping country"
                  required>
                  {shippableCountriesData?.map(country => (
                    <MenuItem value={country.code} key={country.code}>
                      {country.name}
                    </MenuItem>
                  ))}
                </Select>
              )
            }
            name="shippingCountry"
            control={control}
          />
        </div>
      </div>
      <div className="form-field-split">
        <div className="split-input">
          <Label required>Phone Number</Label>
          <Controller
            render={({field}) => (
              <TextField
                value={field.value}
                onChange={field.onChange}
                inputRef={field.ref}
                autoComplete="shipping tel"
                required
              />
            )}
            name="shippingPhoneNumber"
            control={control}
          />
        </div>
      </div>
    </div>
  );
};

export default ShippingDetails;
